import { useEffect, useMemo, useState } from 'react';
import { Image, StyleSheet, TextInput, View } from 'react-native';
import { AlertTriangle, RotateCcw } from 'lucide-react-native';
import type {
  PhotoSearchMatchViewModel,
  PhotoSearchProcessingStep,
} from '../../types/photoSearch';
import { tokens } from '../../theme/tokens';
import { AppBadge } from '../ui/AppBadge';
import { AppButton } from '../ui/AppButton';
import { AppCard } from '../ui/AppCard';
import { AppText } from '../ui/AppText';
import { PhotoSearchRecognitionConfirmCard } from './PhotoSearchRecognitionConfirmCard';
import {
  getInitialPhotoSearchMatchId,
  getSelectedPhotoSearchMatch,
} from '../../utils/photoSearchFlow';

type OCRResultConfirmScreenProps = {
  imageUri: string | null;
  matches: PhotoSearchMatchViewModel[];
  processingStep: PhotoSearchProcessingStep;
  isSubmitting: boolean;
  onSkipMatch: () => void;
  onConfirmText: (text: string, baseMatch: PhotoSearchMatchViewModel | null) => void;
  onRetake: () => void;
};

const getInitialText = (match: PhotoSearchMatchViewModel | null): string =>
  match?.normalizedProblem || match?.ocrText || match?.description || '';

export function OCRResultConfirmScreen({
  imageUri,
  matches,
  processingStep,
  isSubmitting,
  onSkipMatch,
  onConfirmText,
  onRetake,
}: OCRResultConfirmScreenProps) {
  const lowConfidence = processingStep === 'low_confidence';
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(() =>
    getInitialPhotoSearchMatchId(matches)
  );
  const selectedMatch = useMemo(
    () => getSelectedPhotoSearchMatch(matches, selectedMatchId),
    [matches, selectedMatchId]
  );
  const [editableOcrText, setEditableOcrText] = useState(getInitialText(selectedMatch));

  useEffect(() => {
    const initialMatchId = getInitialPhotoSearchMatchId(matches);
    setSelectedMatchId(initialMatchId);
  }, [matches]);

  useEffect(() => {
    setEditableOcrText(getInitialText(selectedMatch));
  }, [selectedMatch?.id]);

  const helperText = useMemo(
    () =>
      lowConfidence
        ? 'OCR 置信度偏低，请先修正或确认题干，确认后才会进入 AI 讲解。'
        : '请确认识别结果，确认后再进入 AI 讲解。',
    [lowConfidence]
  );

  return (
    <View style={styles.stack}>
      <AppCard style={styles.editorCard}>
        <View style={styles.headerRow}>
          <View style={styles.headerText}>
            <AppText variant="subtitle" weight="bold">
              检测到 {matches.length} 道题
            </AppText>
            <AppText variant="caption" tone="muted">
              {matches.length > 1
                ? '请先选择一道题，确认题干后再进入 AI 讲解。'
                : '题干可编辑，确认无误后再进入 AI 讲解。'}
            </AppText>
          </View>
          {lowConfidence ? <AppBadge label="低置信" tone="warning" /> : null}
        </View>

        {lowConfidence ? (
          <View style={styles.warningRow}>
            <AlertTriangle size={18} color={tokens.colors.warning} />
            <AppText variant="caption" tone="muted" style={styles.warningText}>
              低置信结果不会自动进入 AI，需要你显式确认或重新拍摄。
            </AppText>
          </View>
        ) : null}

        {imageUri ? (
          <Image source={{ uri: imageUri }} style={styles.previewImage} resizeMode="cover" />
        ) : null}

        <TextInput
          accessibilityLabel="编辑 OCR 题干"
          value={editableOcrText}
          onChangeText={setEditableOcrText}
          multiline
          placeholder="请确认或修正识别出的题干"
          placeholderTextColor={tokens.colors.textSubtle}
          style={styles.ocrInput}
        />

        <View style={styles.actionRow}>
          <AppButton
            label="重新拍"
            variant="secondary"
            onPress={onRetake}
            leftSlot={<RotateCcw size={18} color={tokens.colors.primary} />}
            style={styles.secondaryAction}
          />
          <AppButton
            label="确认并解析"
            onPress={() => onConfirmText(editableOcrText, selectedMatch)}
            loading={isSubmitting}
            disabled={!selectedMatch || !editableOcrText.trim()}
            style={styles.primaryAction}
          />
        </View>
      </AppCard>

      <PhotoSearchRecognitionConfirmCard
        matches={matches}
        selectedMatchId={selectedMatchId}
        isSubmitting={isSubmitting}
        allowSkip={matches.length === 1 && !lowConfidence}
        helperText={helperText}
        onSelectMatch={(match) => setSelectedMatchId(match.id)}
        onSkipMatch={onSkipMatch}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  stack: {
    gap: tokens.spacing.lg,
  },
  editorCard: {
    gap: tokens.spacing.md,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacing.md,
  },
  headerText: {
    flex: 1,
  },
  warningRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.colors.warningSoft,
    padding: tokens.spacing.md,
  },
  warningText: {
    flex: 1,
  },
  previewImage: {
    width: '100%',
    height: 128,
    borderRadius: tokens.radius.md,
  },
  ocrInput: {
    minHeight: 112,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    backgroundColor: tokens.colors.surfaceMuted,
    color: tokens.colors.text,
    padding: tokens.spacing.md,
    textAlignVertical: 'top',
    fontSize: tokens.fontSize.md,
    lineHeight: 22,
  },
  actionRow: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
  },
  secondaryAction: {
    flex: 1,
  },
  primaryAction: {
    flex: 1,
  },
});
